/**
 * VizProjectHost — runs the ported Visualizer Studio render engine (Scene) for
 * a loaded .vizproject and drives the active scene from the editor's playhead.
 *
 * The engine is a raw Three.js renderer (NOT R3F), so this mounts it into a
 * plain container div and steps it imperatively. Pass a `getTime` callback that
 * returns the current song time in seconds; on each animation frame we find the
 * active scene clip and re-apply its look only when it changes.
 *
 * Audio reactivity is OPTIONAL — by default a silent reader is used (scenes
 * render but don't pulse). Pass `getAudioFrame` to make them react.
 */

import React, { useEffect, useRef } from 'react';
import { Scene } from './visuals/Scene';
import { AudioEngine } from './audio/AudioEngine';
import { findActiveSceneAt } from './studio/timeline';
import { DEFAULT_VIDEO_LAB, DEFAULT_ZONE_REACTION, type AudioFrame, type SceneDescription } from './types';
import type { StudioProject } from './studio/types';
import type { ScenenPreset } from './scenes';

interface Props {
  project: StudioProject;
  /** Current song time in seconds (the editor playhead). */
  getTime: () => number;
  /** Audio reactivity: the host editor's AudioContext + an analyser tapping the
   *  playing song. When both are available the scenes pulse to the music; if
   *  omitted (or null) the scenes render but stay silent/idle. */
  audioContext?: AudioContext | null;
  getAnalyser?: () => AnalyserNode | null;
  /** Override the audio frame entirely (e.g. a synthetic frame derived from the
   *  chart notes for "React to: Notes"). When it returns non-null this is used
   *  instead of the analyser read. */
  getAudioFrame?: (dt: number) => AudioFrame | null;
  /** Manual camera placement (fills the frame): pan x/y as viewport fractions
   *  + zoom. Return null to leave the scene's own camera (auto mode). */
  getCamera?: () => { x: number; y: number; zoom: number } | null;
  className?: string;
  style?: React.CSSProperties;
}

/** Map a scene preset (clip.scene) to the engine's SceneDescription. */
function presetToDescription(p: ScenenPreset): SceneDescription {
  return {
    params: p.params,
    backgroundVideoUrl: p.backgroundVideoUrl ?? null,
    backgroundImageUrl: p.backgroundImageUrl ?? null,
    videoLab: p.videoLab ?? DEFAULT_VIDEO_LAB,
    primitiveLab: p.primitiveLab ?? DEFAULT_VIDEO_LAB,
    textureUrl: p.textureUrl ?? null,
    zoneReaction: p.zoneReaction ?? DEFAULT_ZONE_REACTION,
    camera: p.camera ?? null,
  };
}

const SILENT_FRAME: AudioFrame = {
  frequencies: new Float32Array(1024),
  waveform: new Float32Array(2048),
  bass: 0, mid: 0, treble: 0, level: 0, beat: false, beatIntensity: 0, bpm: 120,
};

export default function VizProjectHost({ project, getTime, audioContext, getAnalyser, getAudioFrame, getCamera, className, style }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const getTimeRef = useRef(getTime);
  const getAnalyserRef = useRef(getAnalyser);
  const getAudioFrameRef = useRef(getAudioFrame);
  const audioCtxRef = useRef(audioContext);
  const getCameraRef = useRef(getCamera);
  getTimeRef.current = getTime;
  getAnalyserRef.current = getAnalyser;
  getAudioFrameRef.current = getAudioFrame;
  audioCtxRef.current = audioContext;
  getCameraRef.current = getCamera;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const clips = project.timeline.sceneTrack.clips;
    if (!clips.length) return;

    // Lazily bind the host's analyser to a reader engine the first time both the
    // context and the analyser exist (the analyser is created on first playback).
    const audio = new AudioEngine();
    let attached = false;
    const readAudio = (dt: number): AudioFrame => {
      // Synthetic frame (e.g. note-driven reactivity) takes priority.
      const synth = getAudioFrameRef.current?.(dt);
      if (synth) return synth;
      if (!attached) {
        const ctx = audioCtxRef.current;
        const an = getAnalyserRef.current?.();
        if (ctx && an) { audio.attachExternalAnalyser(ctx, an); attached = true; }
      }
      return attached ? audio.read(dt) : { ...SILENT_FRAME, bpm: project.bpm || 120 };
    };

    const scene = new Scene(container, clips[0].scene.params, readAudio);
    scene.applySceneDescription(presetToDescription(clips[0].scene));
    scene.start();

    // Drive the active scene from the editor playhead. The Scene runs its own
    // rAF render loop; this lightweight loop only swaps the look on clip change.
    let lastId: string | null = null;
    let raf = 0;
    const tick = () => {
      const t = getTimeRef.current();
      const clip = findActiveSceneAt(project.timeline, t);
      if (clip && clip.id !== lastId) {
        lastId = clip.id;
        scene.applySceneDescription(presetToDescription(clip.scene));
      }
      // Manual camera (re-applied each frame so scene swaps don't reset it).
      const cam = getCameraRef.current?.();
      if (cam) scene.setManualCamera(cam.x, cam.y, cam.zoom);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      scene.dispose();
    };
  }, [project]);

  return <div ref={containerRef} className={className} style={style} />;
}
